/**
 * useLimits Hook
 * 
 * Provides entity limit checking for the current project and user plan.
 * Fetches limits config and entity counts, then provides helper methods
 * to check if creating entities is allowed.
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { collection, onSnapshot } from 'firebase/firestore'
import { db } from '../firebase'
import { useAuth } from '../context/AuthContext'
import { useMonetizationState } from './useMonetizationState'
import {
  subscribeLimitsConfig,
  getLimitsForPlan,
  wouldExceedLimit,
  getRemainingCount as calcRemaining,
  DEFAULT_LIMITS,
} from '../services/limits'
import { getCollectionPath, COLLECTIONS } from '../services/firestore'

/**
 * useLimits Hook
 * 
 * @param {string} projectId - The current project ID
 * @returns {object} Limits state and helper methods
 */
export function useLimits(projectId) {
  const { user } = useAuth()
  const { plan, isProjectPurchased, loading: monetizationLoading } = useMonetizationState()
  
  // Limits configuration from Firestore
  const [limitsConfig, setLimitsConfig] = useState(DEFAULT_LIMITS)
  const [configLoading, setConfigLoading] = useState(true)
  
  // Entity counts for current project
  const [counts, setCounts] = useState({
    characters: 0,
    locations: 0,
    events: 0,
    shots: 0,
    images: 0,
  })
  const [countsLoading, setCountsLoading] = useState(true)

  // Subscribe to limits configuration
  useEffect(() => {
    setConfigLoading(true)
    const unsubscribe = subscribeLimitsConfig((config) => {
      setLimitsConfig(config)
      setConfigLoading(false)
    })
    return unsubscribe
  }, [])

  // Subscribe to entity counts for the project
  useEffect(() => {
    if (!projectId) {
      setCounts({
        characters: 0,
        locations: 0,
        events: 0,
        shots: 0,
        images: 0,
      })
      setCountsLoading(false)
      return
    }

    setCountsLoading(true)
    const unsubscribers = []

    // Entity types to count
    const entityTypes = [
      { key: 'characters', collection: COLLECTIONS.CHARACTERS },
      { key: 'locations', collection: COLLECTIONS.LOCATIONS },
      { key: 'events', collection: COLLECTIONS.EVENTS },
      { key: 'shots', collection: COLLECTIONS.SHOTS },
      { key: 'images', collection: COLLECTIONS.IMAGES },
    ]

    let loadedCount = 0
    const totalToLoad = entityTypes.length

    entityTypes.forEach(({ key, collection: collectionName }) => {
      const collectionPath = getCollectionPath(projectId, collectionName)
      const collectionRef = collection(db, collectionPath)

      const unsubscribe = onSnapshot(
        collectionRef,
        (snapshot) => {
          setCounts((prev) => ({
            ...prev,
            [key]: snapshot.size,
          }))
          loadedCount++
          if (loadedCount >= totalToLoad) {
            setCountsLoading(false)
          }
        },
        (error) => {
          console.error(`[useLimits] Error counting ${key}:`, error)
          loadedCount++
          if (loadedCount >= totalToLoad) {
            setCountsLoading(false)
          }
        }
      )

      unsubscribers.push(unsubscribe)
    })

    return () => {
      unsubscribers.forEach((unsub) => unsub())
    }
  }, [projectId])

  // Determine effective plan for this project
  const effectivePlan = useMemo(() => {
    // Lifetime users always have no limits
    if (plan === 'lifetime') return 'lifetime'
    // Subscription users have no limits
    if (plan === 'subscription') return 'subscription'
    // Check if this specific project is purchased
    if (plan === 'project' && projectId && isProjectPurchased(projectId)) {
      return 'project'
    }
    // Default to free
    return 'free'
  }, [plan, projectId, isProjectPurchased])

  // Get limits for the effective plan
  const limits = useMemo(() => {
    return getLimitsForPlan(limitsConfig, effectivePlan)
  }, [limitsConfig, effectivePlan])

  // Check if user can create more of an entity type
  const canCreate = useCallback(
    (entityType) => {
      // Unlimited plans
      if (limits === null) return true

      const limit = limits[entityType]
      const count = counts[entityType] || 0

      return !wouldExceedLimit(limit, count)
    },
    [limits, counts]
  )

  // Get remaining count for an entity type
  const getRemainingCount = useCallback(
    (entityType) => {
      // Unlimited plans
      if (limits === null) return null

      const limit = limits[entityType]
      const count = counts[entityType] || 0

      return calcRemaining(limit, count)
    },
    [limits, counts]
  )

  // Get limit for an entity type
  const getLimit = useCallback(
    (entityType) => {
      if (limits === null) return null
      return limits[entityType] ?? null
    },
    [limits]
  )

  // Check if any limits are active (i.e., user is on free tier)
  const hasLimits = useMemo(() => {
    return limits !== null
  }, [limits])

  // Check if user is at or over limit for any entity type
  const isAtAnyLimit = useMemo(() => {
    if (!hasLimits) return false
    
    return Object.keys(counts).some((entityType) => {
      const limit = limits[entityType]
      const count = counts[entityType] || 0
      return wouldExceedLimit(limit, count)
    })
  }, [hasLimits, limits, counts])

  // Loading state
  const isLoading = configLoading || countsLoading || monetizationLoading

  return {
    // State
    limits,
    counts,
    effectivePlan,
    hasLimits,
    isAtAnyLimit,
    isLoading,
    
    // Config (for admin display)
    limitsConfig,
    
    // Methods
    canCreate,
    getRemainingCount,
    getLimit,
  }
}

export default useLimits
